import React, { useState, useEffect, useMemo } from 'react'
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom'
import axios from 'axios'
import { toast } from 'react-toastify'
import { PulseLoader } from 'react-spinners'
import useModuleAccess from '../hooks/useModuleAccess'

const API_URL = process.env.REACT_APP_API_URL

function normalizeDescriptions(description) {
  if (!description) return []
  if (Array.isArray(description)) {
    return description.map((entry) => String(entry || '').trim()).filter(Boolean)
  }
  const text = String(description).trim()
  return text ? [text] : []
}

function buildContactsForDisplay(supplier) {
  if (!supplier) return []

  if (Array.isArray(supplier.contacts) && supplier.contacts.length > 0) {
    return supplier.contacts.map((c) => ({
      contactPerson: c.contactPerson || '',
      email: c.email || '',
      phonenumber: c.phonenumber || ''
    }))
  }

  if (supplier.email || supplier.phonenumber || supplier.contactPerson) {
    return [
      {
        contactPerson: supplier.contactPerson || '',
        email: supplier.email || '',
        phonenumber: supplier.phonenumber || ''
      }
    ]
  }

  return []
}

const ViewField = ({ label, children }) => (
  <div className="form-group row align-items-center">
    <label className="col-12 col-md-4 col-form-label">{label}</label>
    <div className="col-12 col-md-8">
      <div className="txtdsply">{children}</div>
    </div>
  </div>
)

function highlightSearchText(text, query) {
  const trimmedQuery = query.trim()
  if (!trimmedQuery) return text

  const escapedQuery = trimmedQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const parts = String(text).split(new RegExp(`(${escapedQuery})`, 'gi'))

  return parts.map((part, index) =>
    part.toLowerCase() === trimmedQuery.toLowerCase() ? (
      <mark
        key={`${part}-${index}`}
        style={{ backgroundColor: '#fff3cd', padding: '0 2px', borderRadius: '2px' }}
      >
        {part}
      </mark>
    ) : (
      part
    )
  )
}

const SupplyMasterView = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { canUpdate } = useModuleAccess('supplier_master')
  const [searchParams] = useSearchParams()
  const [supplier, setSupplier] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [imageUrls, setImageUrls] = useState([]) // Array of download URLs
  const [imageLoading, setImageLoading] = useState(false)
  const [activeTab, setActiveTab] = useState(() =>
    searchParams.get('tab') === 'description' ? 'description' : 'profile'
  )
  const [descriptionSearch, setDescriptionSearch] = useState('')

  useEffect(() => {
    if (searchParams.get('tab') === 'description') {
      setActiveTab('description')
    }
  }, [searchParams])

  useEffect(() => {
    fetchSupplier()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  // Fetch pre-signed URLs for images
  const fetchImageUrls = async (supplierId) => {
    if (!supplierId) return
    
    setImageLoading(true)
    try {
      const payload = {
        action: "get-download-presigned-url",
        supplierId: supplierId
      }
      
      const response = await axios.post(API_URL, payload)
      let responseData = response.data
      
      if (typeof responseData === 'string') {
        try {
          responseData = JSON.parse(responseData)
        } catch (parseError) {
          responseData = response.data
        }
      }
      
      if (responseData.success === true) {
        // Handle both single URL (backward compatibility) and array of files
        if (responseData.files && Array.isArray(responseData.files)) {
          const urls = responseData.files.map(file => ({
            downloadUrl: file.downloadUrl,
            s3Key: file.s3Key,
            originalUrl: file.originalUrl,
            originalFileName: file.originalFileName || null
          }))
          setImageUrls(urls)
        } else if (responseData.downloadUrl) {
          // Backward compatibility: single URL
          setImageUrls([{ downloadUrl: responseData.downloadUrl }])
        } else {
          setImageUrls([])
        }
      } else {
        console.error('Failed to get image URLs:', responseData.message)
        setImageUrls([])
      }
    } catch (error) {
      console.error("Error fetching image URLs:", error)
      setImageUrls([])
    } finally {
      setImageLoading(false)
    }
  }

  // Format date to IST with AM/PM
  const formatDateToIST = (dateString) => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    return date.toLocaleString('en-US', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    })
  }

  const fetchSupplier = async () => {
    if (!id) {
      toast.error("Supplier ID is missing", { autoClose: 2000 })
      navigate('/supplyMaster')
      return
    }

    setIsLoading(true)
    try {
      const payload = {
        action: "get-supplier",
        id: id
      }

      const response = await axios.post(API_URL, payload)
      let responseData = response.data

      if (typeof responseData === 'string') {
        try {
          responseData = JSON.parse(responseData)
        } catch (parseError) {
          responseData = response.data
        }
      }

      if (responseData.success === true && responseData.supplier) {
        setSupplier(responseData.supplier)
        
        // If supplier has attachments, fetch pre-signed URLs
        if (responseData.supplier.attachmentUrl || 
            (responseData.supplier.attachmentUrls && responseData.supplier.attachmentUrls.length > 0)) {
          fetchImageUrls(responseData.supplier.id)
        }
      } else {
        toast.error(responseData.message || "Failed to fetch supplier details", { autoClose: 2000 })
        navigate('/supplyMaster')
      }
    } catch (error) {
      console.error("Fetch supplier error:", error)
      if (error.response && error.response.data) {
        let errorData = error.response.data
        if (typeof errorData === 'string') {
          try {
            errorData = JSON.parse(errorData)
          } catch (e) {
            // Use error message as is
          }
        }
        toast.error(errorData.message || "An error occurred while fetching supplier details", { autoClose: 2000 })
      } else {
        toast.error("Network error. Please check your connection and try again.", { autoClose: 2000 })
      }
      navigate('/supplyMaster')
    } finally {
      setIsLoading(false)
    }
  }

  const descriptions = useMemo(
    () => normalizeDescriptions(supplier?.description),
    [supplier]
  )

  const descriptionEntries = useMemo(
    () =>
      [...descriptions].reverse().map((text, index) => ({
        entryNumber: descriptions.length - index,
        text,
        isLatest: index === 0
      })),
    [descriptions]
  )

  const filteredDescriptionEntries = useMemo(() => {
    const query = descriptionSearch.trim().toLowerCase()
    if (!query) return descriptionEntries
    return descriptionEntries.filter((entry) => entry.text.toLowerCase().includes(query))
  }, [descriptionEntries, descriptionSearch])

  const contacts = useMemo(() => buildContactsForDisplay(supplier), [supplier])

  const handleTabChange = (tab) => {
    setActiveTab(tab)
    if (tab !== 'description') {
      setDescriptionSearch('')
    }
  }

  const tabClass = (tab) => (activeTab === tab ? 'nav-link active' : 'nav-link')

  if (isLoading) {
    return (
      <div>
        <div className="content-header">
          <div className="container-fluid">
            <div className="row mb-2 align-items-center">
              <div className="col-6 col-sm-6">
                <h1 className="m-0 txt_lft">View Supplier</h1>
              </div>
              <div className="col-6 col-sm-6">
                <ol className="breadcrumb justify-content-end">
                  <li className="breadcrumb-item">
                    <Link to="/supplyMaster"><i className="fa-solid fa-arrow-left-long" /> Back</Link>
                  </li>
                </ol>
              </div>
            </div>
          </div>
        </div>
        <div className="content">
          <div className="container-fluid">
            <div className="row">
              <div className="frmwdth_cntr">
                <div className="col-lg-12">
                  <div className="card card-primary card-outline">
                    <div className="card-body text-center py-5">
                      <PulseLoader color="#1199B2" size={10} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!supplier) {
    return (
      <div>
        <div className="content-header">
          <div className="container-fluid">
            <div className="row mb-2 align-items-center">
              <div className="col-6 col-sm-6">
                <h1 className="m-0 txt_lft">View Supplier</h1>
              </div>
              <div className="col-6 col-sm-6">
                <ol className="breadcrumb justify-content-end">
                  <li className="breadcrumb-item">
                    <Link to="/supplyMaster"><i className="fa-solid fa-arrow-left-long" /> Back</Link>
                  </li>
                </ol>
              </div>
            </div>
          </div>
        </div>
        <div className="content">
          <div className="container-fluid">
            <div className="row">
              <div className="frmwdth_cntr">
                <div className="col-lg-12">
                  <div className="card card-primary card-outline">
                    <div className="card-body text-center py-5">
                      <p>Supplier not found</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const showEditButton = canUpdate === true

  return (
    <div>
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2 align-items-center">
            <div className="col-6 col-sm-6">
              <h1 className="m-0 txt_lft">View Supplier</h1>
            </div>
            <div className="col-6 col-sm-6">
              <ol className="breadcrumb justify-content-end">
                <li className="breadcrumb-item">
                  <Link to="/supplyMaster"><i className="fa-solid fa-arrow-left-long" /> Back</Link>
                </li>
              </ol>
            </div>
          </div>
        </div>
      </div>
      <div className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="frmwdth_cntr">
              <div className="col-lg-12">
                <div className="card card-primary card-outline card-tabs">
                  <div className="card-header p-0 pt-1 border-bottom-0">
                    <ul className="nav nav-tabs" role="tablist">
                      <li className="nav-item">
                        <button
                          type="button"
                          className={tabClass('profile')}
                          onClick={() => handleTabChange('profile')}
                        >
                          Profile
                        </button>
                      </li>
                      <li className="nav-item">
                        <button
                          type="button"
                          className={tabClass('description')}
                          onClick={() => handleTabChange('description')}
                        >
                          Description
                          {descriptions.length > 0 ? ` (${descriptions.length})` : ''}
                        </button>
                      </li>
                    </ul>
                  </div>
                  <form className="form-horizontal">
                    <div className="card-body">
                      {activeTab === 'profile' && (
                        <>
                          <ViewField label="Supplier Name">{supplier.clientname || '-'}</ViewField>

                          <div className="form-group row">
                            <label className="col-12 col-md-4 col-form-label">
                              Contacts{contacts.length > 0 ? ` (${contacts.length})` : ''}
                            </label>
                            <div className="col-12 col-md-8">
                              {contacts.length === 0 ? (
                                <div className="txtdsply">-</div>
                              ) : (
                                contacts.map((contact, index) => (
                                  <div
                                    key={`contact-${index}`}
                                    className="border rounded p-3 mb-2"
                                    style={{ background: '#fafbfc' }}
                                  >
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                      <strong style={{ fontSize: '13px' }}>
                                        Contact {index + 1}
                                      </strong>
                                    </div>
                                    <div className="row">
                                      <div className="col-12 col-md-4">
                                        <small className="text-muted d-block">Contact Person</small>
                                        <div className="txtdsply">{contact.contactPerson || '-'}</div>
                                      </div>
                                      <div className="col-12 col-md-4">
                                        <small className="text-muted d-block">Email</small>
                                        <div className="txtdsply">{contact.email || '-'}</div>
                                      </div>
                                      <div className="col-12 col-md-4">
                                        <small className="text-muted d-block">Phone</small>
                                        <div className="txtdsply">{contact.phonenumber || '-'}</div>
                                      </div>
                                    </div>
                                  </div>
                                ))
                              )}
                            </div>
                          </div>
                           <ViewField label="Address">{supplier.address || '-'}</ViewField>
                          <ViewField label="City">{supplier.city || '-'}</ViewField>
                          <ViewField label="State">{supplier.state || '-'}</ViewField>

                          <ViewField label="GST Number">{supplier.gstNumber || '-'}</ViewField>
                          <ViewField label="GST Status">
                            {supplier.gstStatus ? (
                              <span
                                className={`badge ${
                                  String(supplier.gstStatus).toLowerCase() === 'active'
                                    ? 'badge-success'
                                    : 'badge-warning'
                                }`}
                                style={{ fontSize: '90%', fontWeight: 550 }}
                              >
                                {supplier.gstStatus}
                              </span>
                            ) : (
                              '-'
                            )}
                          </ViewField>
                          <ViewField label="Legal Name">{supplier.gstLegalName || '-'}</ViewField>
                          <ViewField label="Trade Name">{supplier.gstTradeName || '-'}</ViewField>
                          <ViewField label="Constitution Type">{supplier.gstConstitutionType || '-'}</ViewField>
                          <ViewField label="Taxpayer Type">
                            {supplier.gstTaxPayerType || supplier.taxPayerType || '-'}
                          </ViewField>
                          <ViewField label="Registration Date">{supplier.gstRegistrationDate || '-'}</ViewField>

                          <ViewField label="Account Number">{supplier.accountNumber || '-'}</ViewField>
                          <ViewField label="IFSC Code">{supplier.ifscCode || '-'}</ViewField>

                          {(supplier.attachmentUrl || (supplier.attachmentUrls && supplier.attachmentUrls.length > 0)) && (
                            <div className="form-group row">
                              <label className="col-12 col-md-4 col-form-label">Attachments</label>
                              <div className="col-12 col-md-8">
                                {imageLoading ? (
                                  <div className="text-center py-3">
                                    <PulseLoader color="#1199B2" size={8} />
                                    <p className="mt-2 text-muted small">Loading attachments...</p>
                                  </div>
                                ) : imageUrls.length > 0 ? (
                                  <div className="d-flex flex-row flex-wrap pt-20 pb-15">
                                    {imageUrls.map((fileData, index) => {
                                      const downloadUrl = fileData.downloadUrl;
                                      const originalFileName = fileData.originalFileName || `Attachment ${index + 1}`;
                                      const isImage = downloadUrl && (
                                        downloadUrl.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp|bmp)$/) ||
                                        fileData.originalUrl?.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp|bmp)$/) ||
                                        originalFileName.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp|bmp)$/)
                                      );
                                      
                                      return (
                                        <div key={index} className="col-12 col-md-6 col-lg-3 mb-3">
                                          <div className="community_imgwrapper">
                                            {isImage ? (
                                              <a 
                                                href={downloadUrl} 
                                                target="_blank" 
                                                rel="noopener noreferrer"
                                                style={{ cursor: 'pointer', display: 'block' }}
                                              >
                                                <div className='community_icon'>
                                                  <img 
                                                    src={downloadUrl} 
                                                    alt={originalFileName} 
                                                    onError={(e) => {
                                                      e.target.style.display = 'none'
                                                      const fallback = e.target.nextElementSibling
                                                      if (fallback) fallback.style.display = 'block'
                                                    }}
                                                  />
                                                  <div style={{ display: 'none', marginTop: '10px' }}>
                                                    <a 
                                                      href={downloadUrl} 
                                                      target="_blank" 
                                                      rel="noopener noreferrer"
                                                      className="btn btn-sm btn-primary btn-block"
                                                    >
                                                      <i className="fa fa-download" /> Download
                                                    </a>
                                                  </div>
                                                </div>
                                                <div>
                                                  <p className="small text-muted mb-0" style={{ 
                                                    fontSize: '0.85rem',
                                                    wordBreak: 'break-word',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 2,
                                                    WebkitBoxOrient: 'vertical'
                                                  }}>
                                                    {originalFileName}
                                                  </p>
                                                </div>
                                              </a>
                                            ) : (
                                              <a 
                                                href={downloadUrl} 
                                                target="_blank" 
                                                rel="noopener noreferrer"
                                                style={{ cursor: 'pointer', display: 'block', textDecoration: 'none' }}
                                              >
                                                <div className='community_icon'>
                                                  <div className="py-3" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                                    <i className="fa-solid fa-file fa-5x text-muted mb-2"></i>
                                                  </div>
                                                </div>
                                                <div className="mt-2">
                                                  <p className="small text-muted mb-0" style={{ 
                                                    fontSize: '0.85rem',
                                                    wordBreak: 'break-word',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 2,
                                                    WebkitBoxOrient: 'vertical'
                                                  }}>
                                                    {originalFileName}
                                                  </p>
                                                </div>
                                              </a>
                                            )}
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                ) : (
                                  <div className="text-muted">
                                    <i className="fa fa-exclamation-triangle" /> Unable to load attachments
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          <ViewField label="Status">
                            <span 
                              className={`badge ${supplier.status === 'active' ? 'badge-success' : 'badge-danger'}`}
                              style={{ fontSize: '90%', fontWeight: 550 }}
                            >
                              {supplier.status || '-'}
                            </span>
                          </ViewField>
                          <ViewField label="Created By">{supplier.createdBy || '-'}</ViewField>
                          <ViewField label="Created At">{formatDateToIST(supplier.createdAt)}</ViewField>
                          <ViewField label="Updated By">
                            {supplier.updatedBy || (supplier.updatedById && supplier.updatedById.name) || '-'}
                          </ViewField>
                          <ViewField label="Updated At">{formatDateToIST(supplier.updatedAt)}</ViewField>
                        </>
                      )}

                      {activeTab === 'description' && (
                        <div>
                          {descriptions.length === 0 ? (
                            <div className="text-center text-muted py-5">
                              <i className="fa-regular fa-note-sticky fa-2x mb-3 d-block" />
                              <p className="mb-0">No description entries yet.</p>
                            </div>
                          ) : (
                            <div className="supplier-description-timeline">
                              <div className="row align-items-center mb-3">
                                <div className="col-12 col-lg-8">
                                  <p className="text-muted small mb-2 mb-lg-0">
                                    {descriptions.length}{' '}
                                    {descriptions.length === 1 ? 'entry' : 'entries'} recorded.
                                  </p>
                                </div>
                                <div className="col-12 col-lg-4 text-lg-right">
                                  <span className="badge badge-secondary">
                                    {descriptionSearch.trim()
                                      ? `Showing ${filteredDescriptionEntries.length} of ${descriptions.length}`
                                      : `${descriptions.length} total`}
                                  </span>
                                </div>
                              </div>

                              <div className="input-group mb-3">
                                <div className="input-group-prepend">
                                  <span className="input-group-text">
                                    <i className="fa fa-search" />
                                  </span>
                                </div>
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder="Search description entries..."
                                  value={descriptionSearch}
                                  onChange={(e) => setDescriptionSearch(e.target.value)}
                                  autoComplete="off"
                                />
                                {descriptionSearch && (
                                  <div className="input-group-append">
                                    <button
                                      type="button"
                                      className="btn btn-outline-secondary"
                                      onClick={() => setDescriptionSearch('')}
                                      title="Clear search"
                                    >
                                      <i className="fa fa-times" />
                                    </button>
                                  </div>
                                )}
                              </div>

                              {filteredDescriptionEntries.length === 0 ? (
                                <div className="text-center text-muted py-4 border rounded bg-light">
                                  <i className="fa fa-search fa-2x mb-3 d-block" />
                                  <p className="mb-1">No entries match your search.</p>
                                  <button
                                    type="button"
                                    className="btn btn-link btn-sm p-0"
                                    onClick={() => setDescriptionSearch('')}
                                  >
                                    Clear search
                                  </button>
                                </div>
                              ) : (
                                <div
                                  className="supplier-description-scroll"
                                  style={{
                                    maxHeight: 'min(60vh, 520px)',
                                    overflowY: 'auto',
                                    paddingRight: '6px'
                                  }}
                                >
                                  {filteredDescriptionEntries.map((entry) => (
                                    <div
                                      key={`entry-${entry.entryNumber}`}
                                      className="card mb-3 shadow-sm"
                                      style={{
                                        borderLeft: entry.isLatest ? '4px solid #1199B2' : '4px solid #dee2e6'
                                      }}
                                    >
                                      <div className="card-body py-3">
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                          <span className="badge badge-light text-dark">
                                            Entry {entry.entryNumber}
                                          </span>
                                          {entry.isLatest && (
                                            <span className="badge badge-info">Latest</span>
                                          )}
                                        </div>
                                        <p
                                          className="mb-0"
                                          style={{
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-word',
                                            lineHeight: 1.6
                                          }}
                                        >
                                          {highlightSearchText(entry.text, descriptionSearch)}
                                        </p>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {showEditButton && (
                      <div className="card-footer text-center">
                        <Link
                          to={`/supplyMasterEdit/${supplier.id}`}
                          className="btn btn-blue mr-2"
                        >
                          Edit
                        </Link>
                      </div>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SupplyMasterView